import React, { useEffect, useState, useCallback, useMemo } from "react";
import Pagination from "../General/Pagination";
import config from "../../Config";
import axios from "axios";
import moment from "moment";
import Spinner from "react-bootstrap/Spinner";
import { useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import StudentMenu from "./StudentMenu";
import RightsCheck from "../General/RightsCheck";
import ThemeCard from "../../UiComponents/common/ThemeCard";
import InputField from "../../UiComponents/common/InputField";
import SelectField from "../../UiComponents/common/SelectField";
import Button from "../../UiComponents/common/Button";
import PageTitle from "../../UiComponents/userpanel/PageTitle";
import Empty from "../../UiComponents/common/Empty";
import {
  Info,
  User,
  Users,
  Phone,
  MapPin,
  Home,
  Smartphone,
  Mail,
  MessageCircle,
  BookOpen,
  Bus,
  FileText,
  Calendar,
  Map,
  Globe,
  IdCard,
  UserCircle,
  Briefcase,
  Award,
  History,
  Search,
  Printer,
  Lock,
} from "lucide-react";

const noop = () => {};

const CHANGED_WRAP = "student-history-field-changed";

/** Normalize scalar values for equality (trim strings; booleans to 0/1). */
function normStr(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "1" : "0";
  return String(v).trim();
}

function dateKey(v) {
  if (!v || !moment(v).isValid()) return "";
  return moment(v).format("YYYY-MM-DD");
}

function genderKey(s) {
  if (!s) return "";
  return s.Gender === true || s.Gender === "true" || s.Gender === 1 ? "M" : "F";
}

function routeKey(s) {
  if (!s) return "";
  const r = s.SelectedRoot;
  const id = s.Current_Route_Id;
  if (r?.Root_Name) return `n:${normStr(r.Root_Name)}`;
  if (r?.Area_Name) return `a:${normStr(r.Area_Name)}`;
  return `id:${id ?? ""}`;
}

function numEq(a, b) {
  const na = a === null || a === undefined || a === "" ? NaN : Number(a);
  const nb = b === null || b === undefined || b === "" ? NaN : Number(b);
  if (Number.isNaN(na) && Number.isNaN(nb)) return true;
  return na === nb;
}

function concessionId(s) {
  if (!s) return 0;
  return Number(s.SelectedConcessionRule?.Concession_Rule_ID ?? s.Concession_Rule_ID ?? 0);
}

/**
 * Compare current snapshot (page N) with the next-older snapshot (page N+1).
 * Returns a Set of field keys that differ.
 */
function computeHistoryDiff(cur, prev) {
  const out = new Set();
  if (!cur || !prev) return out;
  const add = (key, changed) => {
    if (changed) out.add(key);
  };

  add("Registration_No", normStr(cur.Registration_No) !== normStr(prev.Registration_No));
  add("Student_Name", normStr(cur.Student_Name) !== normStr(prev.Student_Name));
  add("Bay_Form_No", normStr(cur.Bay_Form_No) !== normStr(prev.Bay_Form_No));
  add("Form_No", normStr(cur.Form_No) !== normStr(prev.Form_No));
  add("Father_Name", normStr(cur.Father_Name) !== normStr(prev.Father_Name));
  add("Father_CNIC", normStr(cur.Father_CNIC) !== normStr(prev.Father_CNIC));
  add("MotherName", normStr(cur.MotherName) !== normStr(prev.MotherName));
  add("Mother_CNIC", normStr(cur.Mother_CNIC) !== normStr(prev.Mother_CNIC));
  add("FamilyNo", normStr(cur.FamilyNo) !== normStr(prev.FamilyNo));
  add("Date_Of_Birth", dateKey(cur.Date_Of_Birth) !== dateKey(prev.Date_Of_Birth));
  add("Date_Of_Joining", dateKey(cur.Date_Of_Joining) !== dateKey(prev.Date_Of_Joining));
  add("Gender", genderKey(cur) !== genderKey(prev));
  add("Vaccination_Status", normStr(cur.Vaccination_Status) !== normStr(prev.Vaccination_Status));
  add("Vaccination_Date", dateKey(cur.Vaccination_Date) !== dateKey(prev.Vaccination_Date));

  add("Temporary_Address", normStr(cur.Temporary_Address) !== normStr(prev.Temporary_Address));
  add("Permanent_Address", normStr(cur.Permanent_Address) !== normStr(prev.Permanent_Address));
  add("Tehsil", normStr(cur.Tehsil) !== normStr(prev.Tehsil));
  add("District", normStr(cur.District) !== normStr(prev.District));
  add("Contact_Phone", normStr(cur.Contact_Phone) !== normStr(prev.Contact_Phone));
  add("Contact_Mobile", normStr(cur.Contact_Mobile) !== normStr(prev.Contact_Mobile));
  add("Contact_Email", normStr(cur.Contact_Email) !== normStr(prev.Contact_Email));
  add("Guardian_Name", normStr(cur.Guardian_Name) !== normStr(prev.Guardian_Name));
  add("WhatsAppNo", normStr(cur.WhatsAppNo) !== normStr(prev.WhatsAppNo));

  add("FatherMobile", normStr(cur.FatherMobile) !== normStr(prev.FatherMobile));
  add("FatherPassword", normStr(cur.FatherPassword) !== normStr(prev.FatherPassword));
  add("MotherMobile", normStr(cur.MotherMobile) !== normStr(prev.MotherMobile));
  add("MotherPassword", normStr(cur.MotherPassword) !== normStr(prev.MotherPassword));

  add(
    "Session",
    normStr(cur.SelectedClass?.Selected_Session?.Session_Name) !==
      normStr(prev.SelectedClass?.Selected_Session?.Session_Name),
  );
  add(
    "Session_ID",
    Number(cur.SelectedClass?.Session_ID || cur.SelectedClass?.Selected_Session?.Session_ID || 0) !==
      Number(prev.SelectedClass?.Session_ID || prev.SelectedClass?.Selected_Session?.Session_ID || 0),
  );
  add(
    "Wing",
    normStr(cur.SelectedClass?.SelectedWing?.Wing_Name) !== normStr(prev.SelectedClass?.SelectedWing?.Wing_Name),
  );
  add(
    "Wing_ID",
    Number(cur.SelectedClass?.Wing_Id || 0) !== Number(prev.SelectedClass?.Wing_Id || 0),
  );
  add("Class", normStr(cur.SelectedClass?.Class_Name) !== normStr(prev.SelectedClass?.Class_Name));
  add(
    "Class_ID",
    Number(cur.SelectedClass?.Class_Id || 0) !== Number(prev.SelectedClass?.Class_Id || 0),
  );
  add(
    "Section",
    normStr(cur.SelectedSection?.Section_Name) !== normStr(prev.SelectedSection?.Section_Name),
  );
  add(
    "Section_ID",
    Number(cur.SelectedSection?.Section_ID || cur.Section_Id || 0) !==
      Number(prev.SelectedSection?.Section_ID || prev.Section_Id || 0),
  );
  add("Status_Type_Id", Number(cur.Status_Type_Id || 0) !== Number(prev.Status_Type_Id || 0));

  add("Transport_Rute_Status", !!cur.Transport_Rute_Status !== !!prev.Transport_Rute_Status);
  add("Transport_route", routeKey(cur) !== routeKey(prev));
  add("Current_Route_Id", Number(cur.Current_Route_Id || 0) !== Number(prev.Current_Route_Id || 0));

  add("Concession_Rule_ID", concessionId(cur) !== concessionId(prev));
  add("Student_Group_Id", Number(cur.Student_Group_Id || 0) !== Number(prev.Student_Group_Id || 0));

  add("Opening_Balance", !numEq(cur.Opening_Balance, prev.Opening_Balance));
  add("op_balance_type", normStr(cur.op_balance_type) !== normStr(prev.op_balance_type));
  add("Current_Balance", !numEq(cur.Current_Balance, prev.Current_Balance));
  add("Current_Balance_Type", normStr(cur.Current_Balance_Type) !== normStr(prev.Current_Balance_Type));
  add("Arrear_Details", normStr(cur.Arrear_Details) !== normStr(prev.Arrear_Details));

  add("Previous_School_Info", normStr(cur.Previous_School_Info) !== normStr(prev.Previous_School_Info));
  add("NOC_Board", normStr(cur.NOC_Board) !== normStr(prev.NOC_Board));
  add("Remarks", normStr(cur.Remarks) !== normStr(prev.Remarks));

  return out;
}

const StudentHistory = () => {
  const params = useParams();
  const studentId = params.id;

  const [accessKey] = useState(localStorage.getItem("AccessKey"));
  const [userId] = useState(localStorage.getItem("ID"));
  const [memberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [previousSnapshot, setPreviousSnapshot] = useState(null);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    setPageNo(1);
  }, [studentId]);

  const loadHistory = useCallback(async () => {
    if (!studentId) return;

    setLoading(true);
    setPreviousSnapshot(null);

    const basePayload = {
      AccessKey: accessKey,
      UserID: userId,
      MemberType: memberType,
      pageSize,
      Student_ID: parseInt(studentId, 10),
    };

    try {
      const r1 = await axios({
        method: "post",
        url: config.base_url + "Student/GetStudentHistory",
        headers: { "Content-Type": "application/json" },
        data: { ...basePayload, pageNo },
      });

      const tr = r1.data.totalRecords ?? 0;
      setTotalRecords(tr);

      if (r1.data.status_code !== 1 || !r1.data.ListofRecords?.length) {
        setSnapshot(null);
        setPreviousSnapshot(null);
        return;
      }

      const current = r1.data.ListofRecords[0];
      setSnapshot(current);

      if (pageNo < tr) {
        const r2 = await axios({
          method: "post",
          url: config.base_url + "Student/GetStudentHistory",
          headers: { "Content-Type": "application/json" },
          data: { ...basePayload, pageNo: pageNo + 1 },
        });
        if (r2.data.status_code === 1 && r2.data.ListofRecords?.length > 0) {
          setPreviousSnapshot(r2.data.ListofRecords[0]);
        } else {
          setPreviousSnapshot(null);
        }
      } else {
        setPreviousSnapshot(null);
      }
    } catch {
      setSnapshot(null);
      setPreviousSnapshot(null);
      setTotalRecords(0);
      toast.error("Could not load student history.", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoading(false);
    }
  }, [accessKey, userId, memberType, pageNo, pageSize, studentId]);

  useEffect(() => {
    document.title = "Student History";
    loadHistory();
  }, [loadHistory]);

  const changedKeys = useMemo(
    () => computeHistoryDiff(snapshot, previousSnapshot),
    [snapshot, previousSnapshot],
  );

  const dh = (...keys) => (keys.some((k) => changedKeys.has(k)) ? CHANGED_WRAP : "");

  const handlePageChange = (page) => {
    setPageNo(page);
  };

  const fmt = (v, f = "DD/MM/YYYY") => (v && moment(v).isValid() ? moment(v).format(f) : "—");

  const genderLabel = snapshot?.Gender === true || snapshot?.Gender === "true" ? "Male" : "Female";

  const modifiedBy =
    snapshot?.Selected_User?.Full_Name ||
    snapshot?.selected_User?.Full_Name ||
    "";

  const handlePrint = () => window.print();

  const regNo = snapshot?.Registration_No || "";

  return (
    <>
      <style>{`
        .student-history-field-changed .form-control,
        .student-history-field-changed .form-select {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 0.12rem rgba(220, 53, 69, 0.22);
          background-color: #fff8f8;
        }
        .student-history-photo-changed img {
          box-shadow: 0 0 0 3px #dc3545;
        }
      `}</style>
      <RightsCheck />
      <StudentMenu />
      <ToastContainer />
      <div className="container body-content">
        <PageTitle
          title="Student history"
          breadcrumbs={[
            { to: "/Dashboard", label: "Dashboard" },
            { to: "/students/StudentDashboard", label: "Students" },
            { label: "Student history" },
          ]}
        >
          <Button type="link" to="/students/SearchStudents" className="button-white">
            <Search size={16} style={{ marginRight: "8px" }} />
            Search students
          </Button>
          {studentId && (
            <Button type="link" to={`/students/EditStudent/${studentId}`} className="button-white">
              <User size={16} style={{ marginRight: "8px" }} />
              Edit student
            </Button>
          )}
        </PageTitle>

        <div className="page-content">
          <div className="container">
            {loading ? (
              <div
                style={{
                  minHeight: "40vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Spinner animation="border" role="status" />
              </div>
            ) : !snapshot ? (
              <ThemeCard Icon={<History size={20} />} label="No snapshots" detail="This student has no saved history rows yet">
                <Empty label="No history records were found for this student." />
              </ThemeCard>
            ) : (
              <>
                <ThemeCard
                  Icon={<History size={20} />}
                  label="Revision & audit"
                  detail="Each page is one saved snapshot after an add or update (newest first). Red outline compares this snapshot to the immediately older one."
                >
                  <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                      <InputField
                        label="History record #"
                        name="History_ID"
                        value={String(snapshot.History_ID ?? "")}
                        readOnly
                        onChange={noop}
                        Icon={<FileText size={18} />}
                      />
                    </div>
                    <div className="col-md-4">
                      <InputField
                        label="Changed by (user)"
                        name="ModifiedBy"
                        value={modifiedBy || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<User size={18} />}
                      />
                    </div>
                    <div className="col-md-4">
                      <InputField
                        label="Saved at"
                        name="Updated_Date"
                        value={fmt(snapshot.Updated_Date, "DD/MM/YYYY, h:mm:ss a")}
                        readOnly
                        onChange={noop}
                        Icon={<Calendar size={18} />}
                      />
                    </div>
                    <div className="col-md-12">
                      <p className="text-muted mb-1 small">
                        Snapshot {pageNo} of {totalRecords} (page size {pageSize}; use pagination below to move through time).
                      </p>
                      {previousSnapshot ? (
                        <p className="text-danger mb-0 small">
                          Red-highlighted fields differ from the previous (older) revision — the change introduced in this snapshot.
                        </p>
                      ) : (
                        <p className="text-muted mb-0 small">
                          This is the oldest stored revision; there is no earlier snapshot to compare.
                        </p>
                      )}
                    </div>
                  </div>
                </ThemeCard>

                <ThemeCard Icon={<Info size={20} />} label="Basic information" detail="Read-only snapshot of personal details">
                  <div className="row">
                    <div className={`col-md-3 text-center mb-3 mb-md-0 ${dh("Registration_No") ? "student-history-photo-changed" : ""}`}>
                      <div className="photo-preview-wrapper mx-auto" style={{ maxWidth: "200px" }}>
                        <img
                          src={config.StudentImagePath + regNo + ".jpg"}
                          alt=""
                          style={{ width: "100%", borderRadius: "50%", border: "1px solid #dee2e6" }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="small text-muted mt-2">Registration: {regNo || "—"}</div>
                    </div>
                    <div className="col-md-9">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <InputField
                            className={dh("Student_Name")}
                            label="Student name"
                            value={snapshot.Student_Name || ""}
                            readOnly
                            onChange={noop}
                            Icon={<User size={18} />}
                          />
                        </div>
                        <div className="col-md-3">
                          <InputField
                            className={dh("Bay_Form_No")}
                            label="Bay form"
                            value={snapshot.Bay_Form_No || ""}
                            readOnly
                            onChange={noop}
                            Icon={<FileText size={18} />}
                          />
                        </div>
                        <div className="col-md-3">
                          <InputField
                            className={dh("Form_No")}
                            label="Form no."
                            value={snapshot.Form_No || ""}
                            readOnly
                            onChange={noop}
                            Icon={<FileText size={18} />}
                          />
                        </div>
                        <div className="col-md-6">
                          <InputField
                            className={dh("Father_Name")}
                            label="Father name"
                            value={snapshot.Father_Name || ""}
                            readOnly
                            onChange={noop}
                            Icon={<Users size={18} />}
                          />
                        </div>
                        <div className="col-md-6">
                          <InputField
                            className={dh("Father_CNIC")}
                            label="Father CNIC"
                            value={snapshot.Father_CNIC || ""}
                            readOnly
                            onChange={noop}
                            Icon={<IdCard size={18} />}
                          />
                        </div>
                        <div className="col-md-6">
                          <InputField
                            className={dh("MotherName")}
                            label="Mother name"
                            value={snapshot.MotherName || ""}
                            readOnly
                            onChange={noop}
                            Icon={<UserCircle size={18} />}
                          />
                        </div>
                        <div className="col-md-6">
                          <InputField
                            className={dh("Mother_CNIC")}
                            label="Mother CNIC"
                            value={snapshot.Mother_CNIC || ""}
                            readOnly
                            onChange={noop}
                            Icon={<IdCard size={18} />}
                          />
                        </div>
                        <div className="col-md-4">
                          <InputField
                            className={dh("FamilyNo")}
                            label="Family no."
                            value={snapshot.FamilyNo || ""}
                            readOnly
                            onChange={noop}
                            Icon={<Users size={18} />}
                          />
                        </div>
                        <div className="col-md-4">
                          <InputField
                            className={dh("Date_Of_Birth")}
                            label="Date of birth"
                            value={fmt(snapshot.Date_Of_Birth)}
                            readOnly
                            onChange={noop}
                            Icon={<Calendar size={18} />}
                          />
                        </div>
                        <div className="col-md-4">
                          <InputField
                            className={dh("Date_Of_Joining")}
                            label="Date of joining"
                            value={fmt(snapshot.Date_Of_Joining)}
                            readOnly
                            onChange={noop}
                            Icon={<Calendar size={18} />}
                          />
                        </div>
                        <div className="col-md-4">
                          <SelectField
                            className={dh("Gender")}
                            label="Gender"
                            name="Gender"
                            value={genderLabel}
                            disabled
                            onChange={noop}
                            options={[
                              { value: "Male", label: "Male" },
                              { value: "Female", label: "Female" },
                            ]}
                          />
                        </div>
                        <div className="col-md-4">
                          <SelectField
                            className={dh("Vaccination_Status")}
                            label="Vaccination status"
                            name="Vaccination_Status"
                            value={snapshot.Vaccination_Status || "Not Vaccinated"}
                            disabled
                            onChange={noop}
                            options={[
                              { value: "Not Vaccinated", label: "Not Vaccinated" },
                              { value: "Partially Vaccinated", label: "Partially Vaccinated" },
                              { value: "Fully Vaccinated", label: "Fully Vaccinated" },
                            ]}
                          />
                        </div>
                        <div className="col-md-4">
                          <InputField
                            className={dh("Vaccination_Date")}
                            label="Vaccination date"
                            value={snapshot.Vaccination_Date ? fmt(snapshot.Vaccination_Date) : "—"}
                            readOnly
                            onChange={noop}
                            Icon={<Calendar size={18} />}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </ThemeCard>

                <ThemeCard Icon={<Phone size={20} />} label="Contact information" detail="Address and contact details">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <InputField
                        className={dh("Temporary_Address")}
                        label="Temporary address"
                        value={snapshot.Temporary_Address || ""}
                        readOnly
                        onChange={noop}
                        Icon={<MapPin size={18} />}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputField
                        className={dh("Permanent_Address")}
                        label="Permanent address"
                        value={snapshot.Permanent_Address || ""}
                        readOnly
                        onChange={noop}
                        Icon={<Home size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Tehsil")}
                        label="Tehsil"
                        value={snapshot.Tehsil || ""}
                        readOnly
                        onChange={noop}
                        Icon={<Map size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("District")}
                        label="District"
                        value={snapshot.District || ""}
                        readOnly
                        onChange={noop}
                        Icon={<Globe size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Contact_Phone")}
                        label="Contact number"
                        value={snapshot.Contact_Phone || ""}
                        readOnly
                        onChange={noop}
                        Icon={<Phone size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Contact_Mobile")}
                        label="Mobile number"
                        value={snapshot.Contact_Mobile || ""}
                        readOnly
                        onChange={noop}
                        Icon={<Smartphone size={18} />}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputField
                        className={dh("Contact_Email")}
                        label="Email"
                        value={snapshot.Contact_Email || ""}
                        readOnly
                        onChange={noop}
                        Icon={<Mail size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Guardian_Name")}
                        label="Guardian name"
                        value={snapshot.Guardian_Name || ""}
                        readOnly
                        onChange={noop}
                        Icon={<Users size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("WhatsAppNo")}
                        label="WhatsApp number"
                        value={snapshot.WhatsAppNo || ""}
                        readOnly
                        onChange={noop}
                        Icon={<MessageCircle size={18} />}
                      />
                    </div>
                  </div>
                </ThemeCard>

                <ThemeCard Icon={<Lock size={20} />} label="Parent portal (snapshot)" detail="Passwords are not shown; mobiles are from this history row">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <InputField
                        className={dh("FatherMobile")}
                        label="Father mobile (login)"
                        value={snapshot.FatherMobile || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<Smartphone size={18} />}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputField
                        className={dh("FatherPassword")}
                        label="Father password"
                        value={snapshot.FatherPassword ? "•••••••• (stored)" : "—"}
                        readOnly
                        onChange={noop}
                        Icon={<Lock size={18} />}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputField
                        className={dh("MotherMobile")}
                        label="Mother mobile (login)"
                        value={snapshot.MotherMobile || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<Smartphone size={18} />}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputField
                        className={dh("MotherPassword")}
                        label="Mother password"
                        value={snapshot.MotherPassword ? "•••••••• (stored)" : "—"}
                        readOnly
                        onChange={noop}
                        Icon={<Lock size={18} />}
                      />
                    </div>
                  </div>
                </ThemeCard>

                <ThemeCard Icon={<BookOpen size={20} />} label="Session / wing / class / section" detail="Resolved names for this snapshot">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <InputField
                        className={dh("Session", "Session_ID")}
                        label="Session"
                        value={snapshot.SelectedClass?.Selected_Session?.Session_Name || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<Calendar size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Wing", "Wing_ID")}
                        label="Wing"
                        value={snapshot.SelectedClass?.SelectedWing?.Wing_Name || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<BookOpen size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Class", "Class_ID")}
                        label="Class"
                        value={snapshot.SelectedClass?.Class_Name || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<BookOpen size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Section", "Section_ID")}
                        label="Section"
                        value={snapshot.SelectedSection?.Section_Name || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<BookOpen size={18} />}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputField
                        className={dh("Status_Type_Id")}
                        label="Student status"
                        value={snapshot.Status_Type_Name || (snapshot.Status_Type_Id != null ? `ID ${snapshot.Status_Type_Id}` : "—")}
                        readOnly
                        onChange={noop}
                        Icon={<User size={18} />}
                      />
                    </div>
                  </div>
                </ThemeCard>

                <ThemeCard Icon={<Bus size={20} />} label="Transport / concessions / balances" detail="Route, concession rule and fee-related fields from history">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <InputField
                        className={dh("Transport_Rute_Status")}
                        label="Transport enabled"
                        value={snapshot.Transport_Rute_Status ? "Yes" : "No"}
                        readOnly
                        onChange={noop}
                        Icon={<Bus size={18} />}
                      />
                    </div>
                    <div className="col-md-8">
                      <InputField
                        className={dh("Transport_route", "Current_Route_Id")}
                        label="Transport route"
                        value={snapshot.SelectedRoot?.Root_Name || snapshot.SelectedRoot?.Area_Name || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<Bus size={18} />}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputField
                        className={dh("Concession_Rule_ID")}
                        label="Concession rule"
                        value={snapshot.SelectedConcessionRule?.Concession_Rule_Name || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<FileText size={18} />}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputField
                        className={dh("Student_Group_Id")}
                        label="Group"
                        value={snapshot.SelectedGroup?.Group_Name || "—"}
                        readOnly
                        onChange={noop}
                        Icon={<Users size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Opening_Balance")}
                        label="Opening balance"
                        value={snapshot.Opening_Balance != null ? String(snapshot.Opening_Balance) : "—"}
                        readOnly
                        onChange={noop}
                        Icon={<FileText size={18} />}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("op_balance_type")}
                        label="Opening balance type"
                        value={snapshot.op_balance_type || "—"}
                        readOnly
                        onChange={noop}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Current_Balance")}
                        label="Current balance"
                        value={snapshot.Current_Balance != null ? String(snapshot.Current_Balance) : "—"}
                        readOnly
                        onChange={noop}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        className={dh("Current_Balance_Type")}
                        label="Current balance type"
                        value={snapshot.Current_Balance_Type || "—"}
                        readOnly
                        onChange={noop}
                      />
                    </div>
                    <div className="col-md-12">
                      <InputField
                        className={dh("Arrear_Details")}
                        label="Arrear details"
                        value={snapshot.Arrear_Details || ""}
                        readOnly
                        onChange={noop}
                        Icon={<FileText size={18} />}
                      />
                    </div>
                  </div>
                </ThemeCard>

                <ThemeCard Icon={<FileText size={20} />} label="Other details" detail="Remarks and references">
                  <div className="row g-3">
                    <div className="col-md-12">
                      <InputField
                        className={dh("Previous_School_Info")}
                        label="Previous school information"
                        value={snapshot.Previous_School_Info || ""}
                        readOnly
                        onChange={noop}
                        Icon={<Briefcase size={18} />}
                      />
                    </div>
                    <div className="col-md-12">
                      <InputField
                        className={dh("NOC_Board")}
                        label="NOC board"
                        value={snapshot.NOC_Board || ""}
                        readOnly
                        onChange={noop}
                        Icon={<Award size={18} />}
                      />
                    </div>
                    <div className="col-md-12">
                      <InputField
                        className={dh("Remarks")}
                        label="Remarks"
                        value={snapshot.Remarks || ""}
                        readOnly
                        onChange={noop}
                        Icon={<FileText size={18} />}
                      />
                    </div>
                  </div>
                </ThemeCard>
              </>
            )}

            {totalRecords > 0 && (
              <div className="row mt-3 mb-5">
                <div className="col-12">
                  <ThemeCard Icon={<History size={20} />} label="Browse revisions" detail="Move between snapshots">
                    <div className="row align-items-center">
                      <div className="col-md-9">
                        <div className="pagination-wrap">
                          <div className="row align-items-center">
                            <Pagination
                              pageNo={pageNo}
                              pageSize={pageSize}
                              totalRecords={totalRecords}
                              onPageChange={handlePageChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3 mt-3 mt-md-0">
                        <button type="button" className="button button-primary w-100" onClick={handlePrint}>
                          <Printer size={16} style={{ marginRight: "8px" }} />
                          Print
                        </button>
                      </div>
                    </div>
                  </ThemeCard>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentHistory;
